import { Box, Button, Icon } from "@outschool/backpack";
import {
  ParentScheduleQueryQuery,
  ParentScheduleQueryQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faCalendarPlus } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { calculateDateBounds } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import AddCalendarButtonModal from "../AddCalendarButtonModal";
import { ActiveEnrollmentForParentSchedule } from "../CalendarTypes";
import ParentScheduleCalendar from "../ParentScheduleCalendar";
import { DateSelector } from "../ScheduleCalendar";

const Toolbar = ({ view, label, onNavigate, localizer, date }: $TSFixMe) => {
  const { currentUser, userTimeZone } = useSession();
  const { t } = useTranslation(
    "client\\components\\parentSchedule\\ParentAgenda",
  );

  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 24,
      }}
    >
      <DateSelector
        view={view}
        date={date}
        label={label}
        userTimeZone={userTimeZone}
        localizer={localizer}
        onNavigate={onNavigate}
      />
      <AddCalendarButtonModal
        title={t`Calendar: Outschool Schedule`}
        user={currentUser}
        type="parent"
        renderButton={(open: () => void) => (
          <Button onClick={open} startIcon={<Icon icon={faCalendarPlus} />}>
            {t`Subscribe`}
          </Button>
        )}
      />
    </Box>
  );
};

const parentScheduleQuery = gql`
  query ParentScheduleQuery($start: DateTime!, $end: DateTime!) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        ...ParentScheduleCalendar_enrollment
      }
    }
  }
  ${ParentScheduleCalendar.fragments.enrollment}
`;

// needs to be separated out to make ts happy
function isActiveEnrollmentForParentSchedule(
  ae: ActiveEnrollmentForParentSchedule | null,
): ae is ActiveEnrollmentForParentSchedule {
  return ae !== null;
}

export const ParentAgenda = () => {
  const location = useLocation();
  const { view, date } = location.query;
  const { userTimeZone, currentUserIsLoading } = useSession();
  // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
  const { start, end } = calculateDateBounds(date, view, userTimeZone);
  const { loading: parentScheduleLoading, data } = useQueryWithPreviousData<
    ParentScheduleQueryQuery,
    ParentScheduleQueryQueryVariables
  >(parentScheduleQuery, {
    variables: { start, end },
  });

  const { currentUser } = data || {};

  const loading = currentUserIsLoading || parentScheduleLoading;

  if (loading) {
    return null;
  }
  const activeEnrollments =
    currentUser?.activeEnrollments?.filter(
      isActiveEnrollmentForParentSchedule,
    ) ?? [];

  return (
    <ParentScheduleCalendar
      enrollments={activeEnrollments}
      loading={loading}
      start={start}
      ToolbarComponent={Toolbar}
    />
  );
};
